import { useEffect, useId, useState, type CSSProperties } from 'react';
import { minTouchTarget, semanticColor, space, typeStyle } from '@beautyburst/tokens';
import type { CustomerActor } from './App.tsx';
import { customerHref } from './base-path.ts';

export type CustomerNavKey = 'home' | 'shop' | 'stores' | 'search' | 'orders' | 'account' | null;

export interface DesktopNavProps {
  activeNavKey: CustomerNavKey;
  actor: CustomerActor | null;
  bagItemCount: number;
  locationLabel: string | null;
  markets?: readonly { readonly id: string; readonly name: string }[];
  selectedMarketId?: string | null;
  onMarketChange?: (marketId: string) => void;
  marketChangePending?: boolean;
  marketChangeError?: boolean;
  onRetryMarketChange?: () => void;
}

const navFocusClass = 'bb-customer-nav-focus';
const compactViewportQuery = '(max-width: 47.99rem)';

const navTargetStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: minTouchTarget,
  minHeight: minTouchTarget,
  padding: `0 ${space[3]}`,
  color: semanticColor.textLink,
  textDecoration: 'none',
  ...typeStyle('label'),
};

const activeTargetStyle: CSSProperties = {
  ...navTargetStyle,
  color: semanticColor.brandContrast,
  fontWeight: 700,
};

function NavFocusStyles() {
  return (
    <style>
      {`.${navFocusClass}:focus-visible {
        outline: 3px solid ${semanticColor.focusRing};
        outline-offset: 2px;
      }`}
    </style>
  );
}

function bagLabel(itemCount: number): string {
  if (itemCount <= 0) return 'Bag';
  return `Bag, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
}

function compactViewportMatches(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(compactViewportQuery).matches;
}

function useCompactViewport(): boolean {
  const [isCompact, setIsCompact] = useState(compactViewportMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia(compactViewportQuery);
    const updateViewport = () => setIsCompact(mediaQuery.matches);
    updateViewport();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateViewport);
      return () => mediaQuery.removeEventListener('change', updateViewport);
    }

    mediaQuery.addListener(updateViewport);
    return () => mediaQuery.removeListener(updateViewport);
  }, []);

  return isCompact;
}

interface MarketControlProps {
  locationLabel: string | null;
  markets: readonly { readonly id: string; readonly name: string }[];
  selectedMarketId: string | null;
  onMarketChange: ((marketId: string) => void) | undefined;
  marketChangePending: boolean;
  compact?: boolean;
}

function MarketControl({
  locationLabel,
  markets,
  selectedMarketId,
  onMarketChange,
  marketChangePending,
  compact = false,
}: MarketControlProps) {
  if (onMarketChange === undefined) {
    return (
      <span aria-label={locationLabel === null ? 'No market selected' : `Market: ${locationLabel}`}>
        {locationLabel ?? 'Choose market'}
      </span>
    );
  }

  const select = (
    <select
      aria-label="Choose your market"
      data-testid="select-header-market"
      value={selectedMarketId ?? ''}
      aria-busy={marketChangePending}
      disabled={markets.length === 0}
      onChange={(event) => {
        if (event.currentTarget.value !== '') onMarketChange(event.currentTarget.value);
      }}
      style={{
        minHeight: minTouchTarget,
        minWidth: minTouchTarget,
        width: compact ? '100%' : undefined,
        padding: `0 ${space[2]}`,
        color: semanticColor.textPrimary,
        background: semanticColor.surfaceWhite,
        border: `1px solid ${semanticColor.borderDefault}`,
        borderRadius: '0.5rem',
        ...typeStyle('label'),
      }}
    >
      <option value="" disabled>
        {markets.length === 0 ? 'No markets available' : 'Choose market'}
      </option>
      {markets.map((market) => (
        <option key={market.id} value={market.id}>
          {market.name}
        </option>
      ))}
    </select>
  );

  if (compact) return select;

  return (
    <label style={{ display: 'grid', gap: space[1], minWidth: '12rem', ...typeStyle('label') }}>
      Market
      {select}
    </label>
  );
}

interface MarketChangeStatusProps {
  marketChangeError: boolean;
  onRetryMarketChange: (() => void) | undefined;
}

function MarketChangeStatus({ marketChangeError, onRetryMarketChange }: MarketChangeStatusProps) {
  if (!marketChangeError) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: space[2],
        color: semanticColor.textSecondary,
        ...typeStyle('label'),
      }}
    >
      <span>We couldn’t switch markets. Your current market is still selected.</span>
      {onRetryMarketChange === undefined ? null : (
        <button
          type="button"
          className={navFocusClass}
          onClick={onRetryMarketChange}
          style={{ ...navTargetStyle, border: `1px solid ${semanticColor.borderDefault}` }}
        >
          Try again
        </button>
      )}
    </div>
  );
}

function BagLink({ bagItemCount }: Pick<DesktopNavProps, 'bagItemCount'>) {
  return (
    <a
      className={navFocusClass}
      href={customerHref('/cart')}
      aria-label={bagLabel(bagItemCount)}
      style={navTargetStyle}
    >
      Bag
      {bagItemCount > 0 ? (
        <span aria-hidden="true" style={{ marginLeft: space[1] }}>
          {bagItemCount}
        </span>
      ) : null}
    </a>
  );
}

interface CustomerNavLinksProps {
  activeNavKey: CustomerNavKey;
  accountLabel: string;
  bagItemCount: number;
  includeBag?: boolean;
}

function CustomerNavLinks({
  activeNavKey,
  accountLabel,
  bagItemCount,
  includeBag = true,
}: CustomerNavLinksProps) {
  return (
    <>
      <a
        className={navFocusClass}
        href={customerHref('/')}
        aria-current={activeNavKey === 'home' ? 'page' : undefined}
        style={activeNavKey === 'home' ? activeTargetStyle : navTargetStyle}
      >
        Home
      </a>
      <a
        className={navFocusClass}
        href={customerHref('/categories')}
        aria-current={activeNavKey === 'shop' ? 'page' : undefined}
        style={activeNavKey === 'shop' ? activeTargetStyle : navTargetStyle}
      >
        Shop
      </a>
      <a
        className={navFocusClass}
        href={customerHref('/search')}
        aria-current={activeNavKey === 'search' ? 'page' : undefined}
        style={activeNavKey === 'search' ? activeTargetStyle : navTargetStyle}
      >
        Search
      </a>
      <a
        className={navFocusClass}
        href={customerHref('/stores')}
        aria-current={activeNavKey === 'stores' ? 'page' : undefined}
        style={activeNavKey === 'stores' ? activeTargetStyle : navTargetStyle}
      >
        Stores
      </a>
      <a
        className={navFocusClass}
        href={customerHref('/orders')}
        aria-current={activeNavKey === 'orders' ? 'page' : undefined}
        style={activeNavKey === 'orders' ? activeTargetStyle : navTargetStyle}
      >
        Orders
      </a>
      <a className={navFocusClass} href={customerHref('/beauty-burst-plus')} style={navTargetStyle}>
        Beauty Burst+
      </a>
      <a className={navFocusClass} href={customerHref('/order-help')} style={navTargetStyle}>
        Order help
      </a>
      <a
        className={navFocusClass}
        href={customerHref('/account')}
        aria-current={activeNavKey === 'account' ? 'page' : undefined}
        style={activeNavKey === 'account' ? activeTargetStyle : navTargetStyle}
      >
        {accountLabel}
      </a>
      <a className={navFocusClass} href={customerHref('/account/favorites')} style={navTargetStyle}>
        Favorites
      </a>
      {includeBag ? <BagLink bagItemCount={bagItemCount} /> : null}
    </>
  );
}

/** Desktop marketplace navigation. All targets have declared 44px minimums. */
export function DesktopNav({
  activeNavKey,
  actor,
  bagItemCount,
  locationLabel,
  markets = [],
  selectedMarketId = null,
  onMarketChange,
  marketChangePending = false,
  marketChangeError = false,
  onRetryMarketChange,
}: DesktopNavProps) {
  const accountLabel =
    actor?.signedIn === true && actor.displayName !== undefined
      ? `Account, ${actor.displayName}`
      : 'Account';
  const isCompact = useCompactViewport();
  const menuRegionId = useId();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (!isCompact) setIsMenuOpen(false);
  }, [isCompact]);

  // Navigating away must not leave the compact menu covering the new screen.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [activeNavKey]);

  return (
    <nav
      aria-label="Primary"
      className="bb-customer-desktop-nav"
      data-bb-compact-navigation={isCompact ? 'true' : undefined}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: space[1],
        minWidth: 0,
        width: '100%',
      }}
    >
      <NavFocusStyles />
      {isCompact ? (
        <>
          <div data-bb-component="customer-compact-nav-bar">
            <div data-bb-component="customer-compact-market">
              <MarketControl
                locationLabel={locationLabel}
                markets={markets}
                selectedMarketId={selectedMarketId}
                onMarketChange={onMarketChange}
                marketChangePending={marketChangePending}
                compact
              />
            </div>
            <BagLink bagItemCount={bagItemCount} />
            <button
              type="button"
              className={navFocusClass}
              aria-expanded={isMenuOpen}
              aria-controls={menuRegionId}
              onClick={() => setIsMenuOpen((open) => !open)}
              style={{
                ...navTargetStyle,
                background: semanticColor.surfaceWhite,
                border: `1px solid ${semanticColor.borderDefault}`,
              }}
            >
              Menu
            </button>
          </div>
          <div
            id={menuRegionId}
            role="region"
            aria-label="Primary navigation menu"
            data-bb-component="customer-compact-nav-menu"
            hidden={!isMenuOpen}
          >
            <MarketChangeStatus
              marketChangeError={marketChangeError}
              onRetryMarketChange={onRetryMarketChange}
            />
            <div
              data-bb-component="customer-compact-nav-links"
              onClick={() => setIsMenuOpen(false)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setIsMenuOpen(false);
              }}
            >
              <CustomerNavLinks
                activeNavKey={activeNavKey}
                accountLabel={accountLabel}
                bagItemCount={bagItemCount}
                includeBag={false}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <MarketControl
            locationLabel={locationLabel}
            markets={markets}
            selectedMarketId={selectedMarketId}
            onMarketChange={onMarketChange}
            marketChangePending={marketChangePending}
          />
          <MarketChangeStatus
            marketChangeError={marketChangeError}
            onRetryMarketChange={onRetryMarketChange}
          />
          <CustomerNavLinks
            activeNavKey={activeNavKey}
            accountLabel={accountLabel}
            bagItemCount={bagItemCount}
          />
        </>
      )}
    </nav>
  );
}
